import React, { useState, useLayoutEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';

import { Div, Button } from '@pinpoint/design-kit';

import Wrapper from 'src/Wrapper';
import { traitsPropType, stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';

function Nightingale({
	title,
	selected,
	labels,
	series,
	style,
	options,
	formatter,
	relativeColors,
	shade,
	sortByValue,
	onClick,
	showRange: showRangeProp,
	showRangeControl,
}) {
	const [showRange, setShowRange] = useState(showRangeProp);
	const seriesList = [];
	let maxScore = 0;
	let minScore = 50;
	for (let i = 0; i < series.length; i += 1) {
		for (let j = 0; j < labels.length; j += 1) {
			maxScore = maxScore > Math.abs(series[i].values[j] - 50) ? maxScore : Math.abs(series[i].values[j] - 50);
			minScore = minScore < Math.abs(series[i].values[j] - 50) ? minScore : Math.abs(series[i].values[j] - 50);
		}
	}
	const range = maxScore - minScore;
	for (let i = 0; i < series.length; i += 1) {
		const data = [];
		for (let j = 0; j < labels.length; j += 1) {
			const relativeValue = Math.abs(series[i].values[j] - 50) - minScore;
			const baseItemStyle = style.Nightingale.itemStyle(labels[j].factor, 1 - relativeValue / range, shade);
			let selectedItemStyle = {};
			if (labels[j].code === selected) {
				selectedItemStyle = style.Nightingale.selectedItemStyle || {};
			}
			const datum = {
				name: series[i].values[j] >= 50 ? labels[j].highRangeName : labels[j].lowRangeName,
				value: Math.abs(series[i].values[j] - 50),
				trait: labels[j].trait,
				code: labels[j].code,
				rangeName: series[i].values[j] >= 50 ? labels[j].highRangeName : labels[j].lowRangeName,
				rangeDesc: series[i].values[j] >= 50 ? labels[j].highRangeDesc : labels[j].lowRangeDesc,
				percentile: series[i].values[j],
				oceanPosition: labels[j].oceanPosition,
				itemStyle: {
					...baseItemStyle,
					...selectedItemStyle,
				},
			};
			data.push(datum);
		}
		if (sortByValue) {
			data.sort((a, b) => b.value - a.value);
		} else {
			data.sort((a, b) => a.oceanPosition - b.oceanPosition);
		}
		seriesList.push({
			name: series[i].name,
			type: 'pie',
			startAngle: 90,
			radius: '50%',
			center: ['50%', '50%'],
			roseType: 'area',
			avoidLabelOverlap: true,
			label: {
				formatter: formatter(showRange),
				...style.Nightingale.label,
			},
			labelLine: {
				show: true,
				length: 5,
				length2: 2,
				smooth: true,
			},
			data,
			...style.Nightingale.series,
		});
	}
	const visualMap = relativeColors
		? [
				{
					type: 'continuous',
					min: 0,
					max: 50,
					show: false,
					dimension: 'value',
					...style.Nightingale.visualMap(labels[0].factor),
				},
			]
		: [];
	const chartOption = {
		title: {
			text: title,
			...style.global.title,
		},
		textStyle: {
			...style.Nightingale.textStyle,
		},
		backgroundColor: style.global.backgroundColor,
		tooltip: {
			confine: true,
			...style.Nightingale.tooltip,
		},
		toolbox: {
			show: false,
		},
		visualMap,
		series: seriesList,
	};
	const mergedOptions = merge(chartOption, options);

	useLayoutEffect(() => {
		ReactTooltip.rebuild();
	});

	return (
		<Wrapper>
			{showRangeControl ? (
				<Div
					d="flex"
					align="center"
					justify="flex-start"
					h="2.5rem"
					m={{ b: '1rem' }}
					style={{
						backgroundColor: mergedOptions.backgroundColor,
					}}
				>
					<Button
						bg="brand500"
						hoverBg="brand600"
						onClick={() => {
							setShowRange((value) => !value);
						}}
						data-tip={!showRange ? 'View traits as adjectives' : 'View trait names'}
					>
						{showRange ? 'SHOW TRAITS' : 'SHOW RANGES'}
					</Button>
				</Div>
			) : null}
			<ReactEcharts
				ref={(element) => {
					const instance = element ? element.getEchartsInstance() : null;
					if (instance) {
						instance.off('click');
						instance.on('click', 'series', (params) => {
							onClick(params.data.code);
						});
					}
				}}
				style={{
					width: '100%',
					height: showRangeControl ? 'calc(100% - 5rem)' : 'calc(100% - 2.5rem)',
				}}
				option={mergedOptions}
				theme="pinpoint"
			/>
		</Wrapper>
	);
}

Nightingale.propTypes = {
	labels: traitsPropType.isRequired,
	series: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			values: PropTypes.arrayOf(PropTypes.number),
		})
	).isRequired,
	title: PropTypes.string,
	style: stylePropType,
	options: PropTypes.shape({}),
	formatter: PropTypes.func,
	shade: PropTypes.bool,
	relativeColors: PropTypes.bool,
	sortByValue: PropTypes.bool,
	selected: PropTypes.string,
	onClick: PropTypes.func,
	showRange: PropTypes.bool,
	showRangeControl: PropTypes.bool,
};

Nightingale.defaultProps = {
	title: '',
	style: Style1,
	options: {},
	formatter: (showRange) => (showRange ? (params) => params.data.rangeName : (params) => params.data.trait),
	shade: false,
	relativeColors: true,
	sortByValue: true,
	selected: 'px_dim_o',
	onClick: () => null,
	showRange: true,
	showRangeControl: false,
};

export default Nightingale;
